import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Loader2, Upload, Plus, Trash2, Image as ImageIcon, Video, ArrowLeft, Save } from "lucide-react";
import { toast } from "sonner";
import {
  adminCreatePost, adminUpdatePost, adminUpload,
  fetchCategories, fetchPost, fetchTags, formatApiError,
} from "../../lib/api";

const API_ORIGIN = process.env.REACT_APP_BACKEND_URL;
const absUrl = (u) => (u && u.startsWith("/api/uploads/")) ? `${API_ORIGIN}${u}` : u;

const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const emptyPost = {
  slug: "",
  title: "",
  kicker: "",
  excerpt: "",
  categories: [],
  tags: [],
  readTime: 5,
  date: new Date().toISOString().slice(0, 10),
  hero: "",
  featured: false,
  images: [],
  videos: [],
  body: [{ type: "p", text: "" }],
};

export default function AdminPostEdit({ mode }) {
  const { slug } = useParams();
  const nav = useNavigate();
  const isEdit = mode === "edit";
  const [post, setPost] = useState(emptyPost);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [newTag, setNewTag] = useState("");
  const heroFileRef = useRef(null);
  const imageFileRef = useRef(null);
  const videoFileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [cats, tags] = await Promise.all([fetchCategories(), fetchTags()]);
        setCategories(cats);
        setAllTags(tags);
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const p = await fetchPost(slug);
        setPost({
          ...emptyPost,
          ...p,
          body: p.body?.length ? p.body : [{ type: "p", text: "" }],
          images: p.images || [],
          videos: p.videos || [],
        });
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [slug, isEdit]);

  const update = (field, value) => setPost((p) => ({ ...p, [field]: value }));

  const onTitleChange = (v) => {
    update("title", v);
    if (!isEdit && !post.slug) update("slug", slugify(v));
  };

  const toggleCategory = (c) => {
    setPost((p) => ({
      ...p,
      categories: p.categories.includes(c) ? p.categories.filter((x) => x !== c) : [...p.categories, c],
    }));
  };

  const addTag = (t) => {
    const v = (t || newTag).trim().toLowerCase().replace(/\s+/g, "-");
    if (!v) return;
    setPost((p) => (p.tags.includes(v) ? p : { ...p, tags: [...p.tags, v] }));
    setNewTag("");
  };

  const removeTag = (t) => setPost((p) => ({ ...p, tags: p.tags.filter((x) => x !== t) }));

  const addBlock = (type) => setPost((p) => ({ ...p, body: [...p.body, { type, text: "" }] }));
  const updateBlock = (i, text) => setPost((p) => ({
    ...p,
    body: p.body.map((b, idx) => (idx === i ? { ...b, text } : b)),
  }));
  const removeBlock = (i) => setPost((p) => ({ ...p, body: p.body.filter((_, idx) => idx !== i) }));

  const uploadFile = async (file, destField) => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await adminUpload(file);
      if (destField === "hero") {
        update("hero", res.url);
      } else if (destField === "images") {
        setPost((p) => ({ ...p, images: [...p.images, res.url] }));
      } else if (destField === "videos") {
        setPost((p) => ({ ...p, videos: [...p.videos, res.url] }));
      }
      toast.success(`Uploaded ${res.original_name}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const onSave = async () => {
    if (!post.title || !post.slug) {
      toast.error("Title and slug are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...post,
        body: post.body.filter((b) => b.text.trim()),
      };
      if (isEdit) {
        await adminUpdatePost(slug, payload);
        toast.success("Post updated");
      } else {
        await adminCreatePost(payload);
        toast.success("Post created");
      }
      nav("/admin");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-stone-500">Loading…</p>;

  return (
    <div data-testid="admin-post-edit-page" className="max-w-5xl">
      <div className="flex items-center justify-between mb-8">
        <Link to="/admin" className="inline-flex items-center gap-2 text-xs tracking-widest uppercase text-stone-600 hover:text-[#9c4122]">
          <ArrowLeft size={14} /> Back to posts
        </Link>
        <button
          onClick={onSave}
          disabled={saving || uploading}
          data-testid="admin-save-button"
          className="inline-flex items-center gap-2 bg-stone-900 text-white px-6 py-2.5 text-[12px] tracking-[0.18em] uppercase font-semibold hover:bg-[#9c4122] transition-colors disabled:opacity-50"
        >
          {saving ? <><Loader2 className="animate-spin" size={14} /> Saving</> : <><Save size={14} /> {isEdit ? "Save changes" : "Publish"}</>}
        </button>
      </div>

      <p className="kicker text-stone-500">{isEdit ? "Edit post" : "New post"}</p>
      <h1 className="font-serif text-4xl font-light mt-2 text-stone-900">
        {isEdit ? post.title || "Untitled" : "A new story"}
      </h1>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-10">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-7">
          <Field label="Title">
            <input type="text" value={post.title} onChange={(e) => onTitleChange(e.target.value)}
                   data-testid="edit-title-input" className="input-editorial" placeholder="Cinque Terre" />
          </Field>
          <Field label="Slug (URL)">
            <input type="text" value={post.slug} onChange={(e) => update("slug", slugify(e.target.value))}
                   data-testid="edit-slug-input" className="input-editorial" placeholder="cinque-terre" />
          </Field>
          <Field label="Kicker (small caps location)">
            <input type="text" value={post.kicker} onChange={(e) => update("kicker", e.target.value)}
                   data-testid="edit-kicker-input" className="input-editorial" placeholder="Italy · Liguria" />
          </Field>
          <Field label="Excerpt">
            <textarea rows={3} value={post.excerpt} onChange={(e) => update("excerpt", e.target.value)}
                      data-testid="edit-excerpt-input" className="input-editorial resize-none" />
          </Field>

          {/* Hero */}
          <Field label="Hero image">
            <div className="flex gap-3 items-start">
              {post.hero ? (
                <img src={absUrl(post.hero)} alt="hero" className="w-32 h-20 object-cover bg-stone-200" />
              ) : (
                <div className="w-32 h-20 bg-stone-100 border border-dashed border-stone-300 flex items-center justify-center text-stone-400"><ImageIcon size={16} /></div>
              )}
              <div className="flex-1 space-y-2">
                <input type="text" value={post.hero} onChange={(e) => update("hero", e.target.value)}
                       data-testid="edit-hero-url-input" className="input-editorial text-xs" placeholder="Paste URL or upload below" />
                <input ref={heroFileRef} type="file" accept="image/*" data-testid="edit-hero-file-input"
                       className="hidden" onChange={(e) => uploadFile(e.target.files?.[0], "hero")} />
                <button type="button" onClick={() => heroFileRef.current?.click()}
                        className="text-[11px] tracking-[0.2em] uppercase font-semibold text-[#9c4122] inline-flex items-center gap-2 link-underline">
                  <Upload size={12} /> Upload hero
                </button>
              </div>
            </div>
          </Field>

          {/* Body blocks */}
          <Field label="Body">
            <div className="space-y-3">
              {post.body.map((b, i) => (
                <div key={i} data-testid={`edit-block-${i}`} className="border border-stone-200 bg-white">
                  <div className="flex items-center justify-between px-3 py-1.5 border-b border-stone-200 bg-stone-50">
                    <span className="text-[10px] tracking-[0.2em] uppercase text-stone-500 font-semibold">{b.type === "pullquote" ? "Pull quote" : "Paragraph"}</span>
                    <button type="button" onClick={() => removeBlock(i)} className="text-stone-400 hover:text-red-600"><Trash2 size={13} /></button>
                  </div>
                  <textarea rows={b.type === "pullquote" ? 3 : 6} value={b.text}
                            onChange={(e) => updateBlock(i, e.target.value)}
                            data-testid={`edit-block-text-${i}`}
                            className={`w-full px-3 py-3 outline-none resize-vertical ${b.type === "pullquote" ? "italic font-serif text-lg" : "text-[15px] leading-relaxed"}`} />
                </div>
              ))}
              <div className="flex gap-2">
                <button type="button" onClick={() => addBlock("p")} data-testid="edit-add-paragraph" className="inline-flex items-center gap-1 text-xs px-3 py-1.5 border border-stone-300 hover:border-stone-900">
                  <Plus size={12} /> Paragraph
                </button>
                <button type="button" onClick={() => addBlock("pullquote")} data-testid="edit-add-pullquote" className="inline-flex items-center gap-1 text-xs px-3 py-1.5 border border-stone-300 hover:border-stone-900">
                  <Plus size={12} /> Pull quote
                </button>
              </div>
            </div>
          </Field>

          {/* Images */}
          <Field label="Gallery images">
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {post.images.map((src, i) => (
                <div key={i} className="relative aspect-square group">
                  <img src={absUrl(src)} alt="" className="w-full h-full object-cover bg-stone-200" />
                  <button type="button" onClick={() => setPost((p) => ({ ...p, images: p.images.filter((_, idx) => idx !== i) }))}
                          className="absolute top-1 right-1 bg-white/90 text-red-600 p-1 opacity-0 group-hover:opacity-100">
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
              <button type="button" onClick={() => imageFileRef.current?.click()}
                      data-testid="edit-upload-image" className="aspect-square border border-dashed border-stone-300 flex flex-col items-center justify-center gap-1 text-stone-500 hover:border-stone-900">
                <Upload size={14} />
                <span className="text-[10px] tracking-widest uppercase">Add</span>
              </button>
              <input ref={imageFileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadFile(e.target.files?.[0], "images")} />
            </div>
          </Field>

          {/* Videos */}
          <Field label="Videos">
            <div className="space-y-2">
              {post.videos.map((src, i) => (
                <div key={i} className="flex items-center gap-2 border border-stone-200 bg-white p-2">
                  <Video size={16} className="text-stone-500" />
                  <span className="flex-1 truncate text-xs text-stone-700">{src}</span>
                  <button type="button" onClick={() => setPost((p) => ({ ...p, videos: p.videos.filter((_, idx) => idx !== i) }))}
                          className="text-stone-500 hover:text-red-600"><Trash2 size={13} /></button>
                </div>
              ))}
              <button type="button" onClick={() => videoFileRef.current?.click()}
                      data-testid="edit-upload-video" className="inline-flex items-center gap-2 text-xs px-3 py-1.5 border border-stone-300 hover:border-stone-900">
                <Upload size={12} /> Upload video
              </button>
              <input ref={videoFileRef} type="file" accept="video/*" className="hidden" onChange={(e) => uploadFile(e.target.files?.[0], "videos")} />
            </div>
          </Field>
        </div>

        {/* Sidebar */}
        <aside className="space-y-7">
          <Field label="Date">
            <input type="date" value={post.date} onChange={(e) => update("date", e.target.value)}
                   data-testid="edit-date-input" className="input-editorial" />
          </Field>
          <Field label="Read time (min)">
            <input type="number" min="1" max="60" value={post.readTime} onChange={(e) => update("readTime", parseInt(e.target.value) || 1)}
                   data-testid="edit-readtime-input" className="input-editorial" />
          </Field>
          <Field label="Featured on homepage">
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={post.featured} onChange={(e) => update("featured", e.target.checked)} data-testid="edit-featured-checkbox" />
              Show in featured bento
            </label>
          </Field>

          <Field label="Categories">
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => (
                <button key={c.slug} type="button" onClick={() => toggleCategory(c.slug)}
                        data-testid={`edit-category-${c.slug}`}
                        className={`px-2.5 py-1 text-[11px] uppercase tracking-[0.1em] border transition-colors ${post.categories.includes(c.slug) ? "bg-stone-900 text-white border-stone-900" : "bg-transparent text-stone-700 border-stone-300 hover:border-stone-900"}`}>
                  {c.name}
                </button>
              ))}
            </div>
          </Field>

          <Field label="Tags">
            <div className="flex flex-wrap gap-2 mb-2">
              {post.tags.map((t) => (
                <span key={t} data-testid={`edit-tag-chip-${t}`} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-full bg-stone-900 text-white">
                  {t}
                  <button type="button" onClick={() => removeTag(t)} className="hover:text-[#fbeae6]"><Trash2 size={10} /></button>
                </span>
              ))}
            </div>
            <input
              type="text"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
              data-testid="edit-tag-input"
              className="input-editorial text-sm"
              placeholder="Type a tag and press Enter"
              list="existing-tags"
            />
            <datalist id="existing-tags">
              {allTags.map((t) => <option key={t.slug} value={t.slug} />)}
            </datalist>
          </Field>
        </aside>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <p className="kicker text-stone-500 mb-2">{label}</p>
      {children}
    </div>
  );
}
